import React, { useState, useCallback } from 'react';
import { authApi } from '../services/api/authApi';
import './ProfilePage.css';

// ─── Helpers ──────────────────────────────────────────────────

function getInitials(fullName, email) {
  if (fullName) {
    return fullName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return (email?.charAt(0) || 'U').toUpperCase();
}

// ─── Icons (inline SVG, no external deps) ─────────────────────

const IconUser = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M2 14c0-3.31 2.69-5 6-5s6 1.69 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconMail = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="4" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M2 6l6 4 6-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconLock = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M11 7V5C11 2.79 9.66 1 8 1C6.34 1 5 2.79 5 5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 11V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <rect x="2.5" y="7" width="11" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const IconClinic = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M2 14V7l6-5 6 5v7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="5.5" y="10" width="5" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const IconBadge = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 1l1.5 3H13l-2.75 2 1 3.5L8 7.5 4.75 9.5l1-3.5L3 4h3.5L8 1z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 13h6M6 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconBriefcase = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="6" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 6V4.5A1.5 1.5 0 016.5 3h3A1.5 1.5 0 0111 4.5V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M2 10h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconEdit = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M11.5 2.5l2 2-9 9H2.5v-2l9-9z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 8l4 4 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconBack = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Editable Field ────────────────────────────────────────────

function EditableField({ label, icon, value, onSave, placeholder, hint, type = 'text' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const openEdit = () => {
    setDraft(value || '');
    setError('');
    setSaved(false);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError('');
  };

  const save = async () => {
    setError('');
    // Basic validation
    if (!draft.trim()) {
      setError('This field cannot be empty.');
      return;
    }
    if (type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') cancel();
  };

  return (
    <div className="profile-field">
      <div className="profile-field-left">
        <span className="profile-field-label">
          {icon}
          {label}
        </span>

        {editing ? (
          <div className="profile-inline-edit">
            <input
              className="profile-inline-input"
              type={type}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoFocus
              disabled={saving}
            />
            {hint && !error && <span className="profile-field-hint">{hint}</span>}
            {error && (
              <span className="profile-field-error">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 5v4M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {error}
              </span>
            )}
            <div className="profile-inline-actions">
              <button className="profile-save-btn" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="profile-cancel-btn" onClick={cancel} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <span className={`profile-field-value ${!value ? 'empty' : ''}`}>
              {value || 'Not set'}
            </span>
            {saved && (
              <span className="profile-save-success">
                <IconCheck /> Saved
              </span>
            )}
          </>
        )}
      </div>

      {!editing && (
        <button className="profile-field-edit-btn" onClick={openEdit}>
          <IconEdit />
          {value ? 'Edit' : 'Set'}
        </button>
      )}
    </div>
  );
}

// ─── Password Field ────────────────────────────────────────────
// Sends a password reset email — consistent with the rest of the portal.

function PasswordField({ onSave }) {
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    setError('');
    setSending(true);
    try {
      await onSave();
      setConfirming(false);
      setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="profile-field">
      <div className="profile-field-left">
        <span className="profile-field-label">
          <IconLock />
          Password
        </span>

        {confirming ? (
          <div className="profile-inline-edit">
            <span className="profile-field-hint">
              A password reset link will be sent to your email address.
            </span>
            {error && (
              <span className="profile-field-error">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 5v4M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {error}
              </span>
            )}
            <div className="profile-inline-actions">
              <button className="profile-save-btn" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending…' : 'Send Reset Link'}
              </button>
              <button className="profile-cancel-btn" onClick={() => { setConfirming(false); setError(''); }} disabled={sending}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <span className="profile-field-value">••••••••</span>
            {sent && (
              <span className="profile-save-success">
                <IconCheck /> Reset link sent to your email
              </span>
            )}
          </>
        )}
      </div>

      {!confirming && (
        <button className="profile-field-edit-btn" onClick={() => { setSent(false); setConfirming(true); }}>
          <IconEdit />
          Change
        </button>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────

// Maps frontend camelCase keys to backend snake_case field names
const FIELD_MAP = {
  fullName: 'full_name',
  clinicName: 'clinic_name',
  licenseNumber: 'license_number',
  jobTitle: 'job_title',
};

export default function ProfilePage({ currentUser, onBack, onProfileUpdate }) {
  // Local profile state — will be synced to backend later
  const [profile, setProfile] = useState({
    fullName: currentUser?.full_name || '',
    email: currentUser?.email || '',
    clinicName: currentUser?.clinic_name || '',
    licenseNumber: currentUser?.license_number || '',
    jobTitle: currentUser?.job_title || '',
  });

  const handleSave = useCallback(
    (field) => async (value) => {
      const backendField = FIELD_MAP[field];
      const data = await authApi.updateProfile({ [backendField]: value });
      // Sync local state and parent from the server response
      const updated = {
        fullName: data.user.full_name || '',
        email: data.user.email || '',
        clinicName: data.user.clinic_name || '',
        licenseNumber: data.user.license_number || '',
        jobTitle: data.user.job_title || '',
      };
      setProfile(updated);
      if (onProfileUpdate) onProfileUpdate(data.user);
    },
    [onProfileUpdate]
  );

  const handlePasswordSave = useCallback(async () => {
    // Sends a password reset email — same flow as "Change Password" in the dropdown
    const response = await fetch(
      (await import('../config')).apiUrl('/api/auth/change-password'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email }),
      }
    );
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to send reset email.');
  }, [profile.email]);

  const initials = getInitials(profile.fullName, profile.email);

  return (
    <div className="profile-page">
      <div className="profile-page-inner">
      {/* Back */}
      <button className="profile-back-btn" onClick={onBack}>
        <IconBack />
        Back to Portal
      </button>

      {/* Header */}
      <div className="profile-page-header">
        <div className="profile-page-avatar">{initials}</div>
        <div className="profile-page-header-text">
          <h1>{profile.fullName || 'Your Profile'}</h1>
          <p>{profile.jobTitle || 'Doctor'} · {profile.clinicName || 'Algodentra'}</p>
        </div>
      </div>

      {/* Content */}
      <div className="profile-page-content">

        {/* Personal Information */}
        <div className="profile-section">
          <p className="profile-section-title">Personal Information</p>

          <EditableField
            label="Full Name"
            icon={<IconUser />}
            value={profile.fullName}
            onSave={handleSave('fullName')}
            placeholder="e.g. Dr. Sarah Chen"
            hint="This name will appear on your generated SOAP notes."
          />

          <EditableField
            label="Email Address"
            icon={<IconMail />}
            value={profile.email}
            onSave={handleSave('email')}
            placeholder="e.g. doctor@clinic.com"
            type="email"
            hint="Changing your email will require re-verification."
          />

          <PasswordField onSave={handlePasswordSave} />
        </div>

        {/* Professional Information */}
        <div className="profile-section">
          <p className="profile-section-title">Professional Information</p>

          <EditableField
            label="Clinic / Practice Name"
            icon={<IconClinic />}
            value={profile.clinicName}
            onSave={handleSave('clinicName')}
            placeholder="e.g. Sunshine Dental Clinic"
          />

          <EditableField
            label="License Number"
            icon={<IconBadge />}
            value={profile.licenseNumber}
            onSave={handleSave('licenseNumber')}
            placeholder="e.g. RCDSO-123456"
            hint="Your regulatory dental license number (e.g. RCDSO, CDSBC)."
          />

          <EditableField
            label="Job Title"
            icon={<IconBriefcase />}
            value={profile.jobTitle}
            onSave={handleSave('jobTitle')}
            placeholder="e.g. General Dentist, Orthodontist"
          />
        </div>

      </div>
      </div>
    </div>
  );
}
